import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from './ui/button';
import { ApiService } from '../utils/api';
import { formatCurrency } from '../utils/currency';
import { formatDateTime } from '../utils/datetime';
import { useToast } from './Toast';
import { RefreshCw, X } from 'lucide-react';

const STATUS_STYLE = {
  PENDING: 'bg-amber-100 text-amber-800',
  SUCCESS: 'bg-green-100 text-green-800',
  FAILED: 'bg-red-100 text-red-800',
};

const MODES = [
  ['FULL', 'Full refund', 'Registration amount and the platform fee'],
  ['EXCLUDE_FEE', 'Platform fee not refundable', 'Refund only the registration amount'],
  ['CUSTOM', 'Custom amount', 'Any amount up to what is left'],
];

/** Refund a registration's payment through Cashfree/PayU, or record one paid back offline. */
const RefundDialog = ({ eventId, userId, onClose, onRefunded }) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { data: summary, isLoading, error } = useQuery({
    queryKey: ['refunds', eventId, userId],
    queryFn: () => ApiService.getRegistrationRefunds(eventId, userId),
  });
  const [mode, setMode] = useState('FULL');
  const [amount, setAmount] = useState('');
  const [remarks, setRemarks] = useState('');
  const [reference, setReference] = useState('');
  const [cancelRegistration, setCancelRegistration] = useState(true);
  const [notifyAttendee, setNotifyAttendee] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const currency = summary?.currency || 'INR';

  useEffect(() => {
    if (summary && summary.feeRefundable <= 0 && mode === 'EXCLUDE_FEE') setMode('FULL');
  }, [summary, mode]);

  const refundAmount = useMemo(() => {
    if (!summary) return 0;
    if (mode === 'FULL') return summary.refundable;
    if (mode === 'EXCLUDE_FEE') return summary.registrationRefundable;
    return Number(amount) || 0;
  }, [summary, mode, amount]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['refunds', eventId, userId] });
    queryClient.invalidateQueries({ queryKey: ['registration', eventId, userId] });
    queryClient.invalidateQueries({ queryKey: ['registrations', eventId] });
    queryClient.invalidateQueries({ queryKey: ['registrationHistory', eventId, userId] });
  };

  const submit = useMutation({
    mutationFn: () => ApiService.createRegistrationRefund(eventId, userId, {
      mode,
      amount: mode === 'CUSTOM' ? Number(amount) : null,
      remarks,
      reference: summary?.gatewayRefund ? null : reference,
      cancelRegistration,
      notifyAttendee,
    }),
    onSuccess: (refund) => {
      refresh();
      onRefunded?.();
      showSuccess(refund.status === 'SUCCESS'
        ? `Refund of ${formatCurrency(refund.amount, currency)} completed`
        : `Refund of ${formatCurrency(refund.amount, currency)} initiated; it usually completes in 5–7 working days`);
      setRemarks('');
      setAmount('');
      setReference('');
      setConfirming(false);
    },
    onError: (err) => { setConfirming(false); showError(err?.message || 'Refund failed'); },
  });

  const sync = useMutation({
    mutationFn: (refundId) => ApiService.syncRefund(eventId, refundId),
    onSuccess: (refund) => { refresh(); showSuccess(`Refund ${refund.id}: ${refund.status.toLowerCase()}`); },
    onError: (err) => showError(err?.message || 'Could not check the refund'),
  });

  const canSubmit = summary && summary.refundable >= 1 && remarks.trim().length >= 3 && refundAmount >= 1
    && refundAmount <= summary.refundable + 0.001 && (summary.gatewayRefund || reference.trim());

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Process refund">
      <div className="w-full max-w-lg max-h-[90vh] overflow-auto rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="text-lg font-semibold">Process refund · {userId}</h3>
          <button type="button" aria-label="Close" onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-4 p-5 text-sm">
          {isLoading && <p className="text-gray-500">Loading payment…</p>}
          {error && <p className="text-red-600">{error.message}</p>}
          {summary && (
            <>
              <div className="rounded-md border bg-gray-50 p-3 space-y-1">
                <div className="flex justify-between"><span>Paid ({summary.gatewayRefund ? summary.provider : 'offline'})</span><b>{formatCurrency(summary.paid, currency)}</b></div>
                <div className="flex justify-between text-gray-600"><span>Registration</span><span>{formatCurrency(summary.registrationPaid, currency)}</span></div>
                {summary.feePaid > 0 && <div className="flex justify-between text-gray-600"><span>Platform fee</span><span>{formatCurrency(summary.feePaid, currency)}</span></div>}
                {summary.refunded > 0 && <div className="flex justify-between text-gray-600"><span>Already refunded</span><span>−{formatCurrency(summary.refunded, currency)}</span></div>}
                <div className="flex justify-between font-medium"><span>Refundable now</span><span>{formatCurrency(summary.refundable, currency)}</span></div>
                {summary.paymentId && <p className="text-xs text-gray-500">Payment {summary.paymentId} · order {summary.orderId}</p>}
              </div>

              {summary.refundable >= 1 ? (
                <>
                  <fieldset className="space-y-2">
                    <legend className="font-medium mb-1">How much to refund</legend>
                    {MODES.filter(([value]) => value !== 'EXCLUDE_FEE' || summary.feeRefundable > 0).map(([value, title, hint]) => (
                      <label key={value} className={`flex items-start gap-2 rounded border p-2 cursor-pointer ${mode === value ? 'border-blue-600 bg-blue-50' : 'border-gray-200'}`}>
                        <input type="radio" name="refund-mode" className="mt-1" checked={mode === value} onChange={() => setMode(value)} />
                        <span><span className="font-medium">{title}</span> <span className="text-gray-500">— {hint}</span></span>
                      </label>
                    ))}
                    {mode === 'CUSTOM' && (
                      <input type="number" min="1" step="0.01" max={summary.refundable} value={amount} onChange={(e) => setAmount(e.target.value)}
                        className="h-9 w-40 rounded-md border border-gray-300 px-2" placeholder={`Up to ${summary.refundable}`} aria-label="Refund amount" />
                    )}
                    <p className="text-gray-700">Refund amount: <b>{formatCurrency(refundAmount, currency)}</b></p>
                  </fieldset>

                  <label className="block">
                    <span className="font-medium">Reason / remarks *</span>
                    <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} maxLength={300} rows={2}
                      className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1" placeholder="e.g. Attendee can't attend; event cancelled" />
                  </label>
                  {!summary.gatewayRefund && (
                    <label className="block">
                      <span className="font-medium">Bank / UPI reference *</span>
                      <input value={reference} onChange={(e) => setReference(e.target.value)} maxLength={120}
                        className="mt-1 h-9 w-full rounded-md border border-gray-300 px-2" placeholder="UTR of the money you sent back" />
                      <span className="text-xs text-gray-500">This payment was made offline, so pay it back yourself and record the reference here.</span>
                    </label>
                  )}
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={cancelRegistration} onChange={(e) => setCancelRegistration(e.target.checked)} />
                    Cancel the registration (QR stops working)
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={notifyAttendee} onChange={(e) => setNotifyAttendee(e.target.checked)} />
                    Email the attendee about the refund
                  </label>
                  {confirming ? (
                    <div className="rounded-md border border-red-200 bg-red-50 p-3 space-y-2" role="alert">
                      <p className="font-medium text-red-800">
                        Refund {formatCurrency(refundAmount, currency)} to {userId}?
                      </p>
                      <ul className="list-disc pl-5 text-red-800/90 space-y-0.5">
                        <li>{summary.gatewayRefund
                          ? `${summary.provider === 'PAYU' ? 'PayU' : 'Cashfree'} sends it back to the original payment method; this can't be undone.`
                          : 'Recorded as paid back offline with the reference above.'}</li>
                        {mode === 'EXCLUDE_FEE' && <li>The platform fee of {formatCurrency(summary.feeRefundable, currency)} is kept.</li>}
                        {cancelRegistration && <li>The registration is cancelled and its QR code stops working.</li>}
                        {notifyAttendee && <li>The attendee is emailed about the refund.</li>}
                      </ul>
                      <div className="flex justify-end gap-2 pt-1">
                        <Button variant="outline" onClick={() => setConfirming(false)} disabled={submit.isPending}>Go back</Button>
                        <Button className="bg-red-600 hover:bg-red-700" onClick={() => submit.mutate()} disabled={submit.isPending}>
                          {submit.isPending ? 'Refunding…' : 'Confirm refund'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={onClose}>Close</Button>
                      <Button className="bg-red-600 hover:bg-red-700" disabled={!canSubmit} onClick={() => setConfirming(true)}>
                        Refund {formatCurrency(refundAmount, currency)}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-gray-600">Nothing is left to refund on this registration.</p>
              )}

              {summary.refunds?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Refunds</h4>
                  <ul className="divide-y rounded border">
                    {summary.refunds.map((r) => (
                      <li key={r.id} className="p-2 space-y-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-xs">{r.id}</span>
                          <span className={`rounded-full px-2 py-0.5 text-xs ${STATUS_STYLE[r.status] || ''}`}>{r.status.toLowerCase()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>{formatCurrency(r.amount, currency)}{r.feePart > 0 ? ` (incl. fee ${formatCurrency(r.feePart, currency)})` : ''} · {r.provider}</span>
                          <span className="text-xs text-gray-500">{formatDateTime(r.createdAt)}</span>
                        </div>
                        <p className="text-xs text-gray-600">{r.remarks}</p>
                        {r.reference && <p className="text-xs text-gray-500">Reference {r.reference}</p>}
                        {r.error && <p className="text-xs text-red-600">{r.error}</p>}
                        {r.merchantNote && <p className="text-xs text-amber-700">{r.merchantNote}</p>}
                        {r.status === 'PENDING' && r.provider !== 'MANUAL' && (
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => sync.mutate(r.id)} disabled={sync.isPending}>
                            <RefreshCw className="h-3 w-3 mr-1" /> Check status
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RefundDialog;
